/* LATTICE motion layer · Pixelated Physics
   1) Hero field: a lattice of pixels lit by two-source wave interference
      (plus a weak third source at the cursor). Intensity ∝ |ψ1 + ψ2|².
   2) Card tilt + cursor glow on [data-lat-tilt] (fine pointers only).
   3) Hero parallax vars (--px, --py) for the floating art.
   Respects prefers-reduced-motion (one static frame, no tilt), pauses offscreen
   and in hidden tabs, caps devicePixelRatio at 1.5. No dependencies. */
(function () {
  'use strict';
  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');

  function Field(canvas, host) {
    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
    var w = 0, h = 0, dpr = 1, pts = [], raf = 0, onscreen = true, running = false;
    var mx = -9999, my = -9999, tx = -9999, ty = -9999, cursorW = 0, cursorT = 0;
    var LAMBDA = 84, K = (2 * Math.PI) / LAMBDA, OMEGA = 1.7; // px, rad/s
    var LEVELS = 10;
    var bucketsC = [], bucketsM = [];
    var start = performance.now();

    function build() {
      var s = w < 750 ? 18 : 16; // lattice pitch in px (multiple of 2 for crisp pixels)
      pts = [];
      for (var y = s / 2; y < h; y += s) for (var x = s / 2; x < w; x += s) pts.push(x, y);
    }
    function resize() {
      var r = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = Math.max(1, r.width); h = Math.max(1, r.height);
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      draw(RM.matches ? 0.6 : (performance.now() - start) / 1000);
    }
    function draw(t) {
      ctx.clearRect(0, 0, w, h);
      var mobile = w < 750;
      // sources drift slowly on small orbits so the fringes breathe
      var s1x = w * (mobile ? 0.2 : 0.62) + Math.cos(t * 0.21) * 24, s1y = h * 0.28 + Math.sin(t * 0.17) * 18;
      var s2x = w * (mobile ? 0.8 : 0.86) + Math.sin(t * 0.19) * 24, s2y = h * 0.74 + Math.cos(t * 0.23) * 18;
      mx += (tx - mx) * 0.08; my += (ty - my) * 0.08;
      cursorW += ((cursorT) - cursorW) * 0.05;
      var i, k, x, y, d1, d2, d3, a, I, lvl;
      for (k = 0; k < LEVELS; k++) { bucketsC[k] = []; bucketsM[k] = []; }
      var phase = OMEGA * t;
      var fadeL = mobile ? 0 : w * 0.18, fadeR = mobile ? 1 : w * 0.55;
      for (i = 0; i < pts.length; i += 2) {
        x = pts[i]; y = pts[i + 1];
        d1 = Math.sqrt((x - s1x) * (x - s1x) + (y - s1y) * (y - s1y));
        d2 = Math.sqrt((x - s2x) * (x - s2x) + (y - s2y) * (y - s2y));
        // amplitudes fall off as 1/sqrt(r) for circular waves in 2D (softened)
        a = Math.cos(K * d1 - phase) / Math.sqrt(1 + d1 / 220) + Math.cos(K * d2 - phase) / Math.sqrt(1 + d2 / 220);
        if (cursorW > 0.01) {
          d3 = Math.sqrt((x - mx) * (x - mx) + (y - my) * (y - my));
          a += cursorW * 0.9 * Math.cos(K * d3 - phase * 1.3) * Math.exp(-d3 / 260);
        }
        I = (a * a) / 4; // normalised intensity, ~0..1
        if (I > 1) I = 1;
        if (!mobile) { // keep the copy column calm
          var f = (x - fadeL) / (fadeR - fadeL); f = f < 0 ? 0 : f > 1 ? 1 : f;
          I *= 0.25 + 0.75 * f;
        } else { I *= 0.7; }
        lvl = (I * (LEVELS - 1)) | 0;
        if (lvl < 1) continue;
        (a >= 0 ? bucketsC : bucketsM)[lvl].push(x, y);
      }
      for (k = 1; k < LEVELS; k++) {
        var alpha = 0.1 + (k / LEVELS) * 0.8, size = 1.2 + (k / LEVELS) * 2.8, half = size / 2, arr;
        arr = bucketsC[k];
        if (arr.length) { ctx.fillStyle = 'rgba(92,242,255,' + alpha + ')'; ctx.beginPath(); for (i = 0; i < arr.length; i += 2) ctx.rect(arr[i] - half, arr[i + 1] - half, size, size); ctx.fill(); }
        arr = bucketsM[k];
        if (arr.length) { ctx.fillStyle = 'rgba(178,140,255,' + (alpha * 0.85) + ')'; ctx.beginPath(); for (i = 0; i < arr.length; i += 2) ctx.rect(arr[i] - half, arr[i + 1] - half, size, size); ctx.fill(); }
      }
    }
    function loop(now) {
      if (!running) return;
      draw((now - start) / 1000);
      raf = requestAnimationFrame(loop);
    }
    function sync() {
      var should = onscreen && !document.hidden && !RM.matches;
      if (should && !running) { running = true; raf = requestAnimationFrame(loop); }
      else if (!should && running) { running = false; cancelAnimationFrame(raf); }
    }
    host.addEventListener('pointermove', function (e) {
      var r = canvas.getBoundingClientRect();
      tx = e.clientX - r.left; ty = e.clientY - r.top; cursorT = 1;
      if (mx < -999) { mx = tx; my = ty; }
    }, { passive: true });
    host.addEventListener('pointerleave', function () { cursorT = 0; }, { passive: true });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { onscreen = es[0].isIntersecting; sync(); }, { threshold: 0 }).observe(host);
    }
    document.addEventListener('visibilitychange', sync);
    if (RM.addEventListener) RM.addEventListener('change', function () { resize(); sync(); });
    var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(resize, 150); }, { passive: true });
    resize(); sync();
  }

  function parallax(host) {
    if (RM.matches || !FINE.matches) return;
    var raf = 0, px = 0, py = 0;
    host.addEventListener('pointermove', function (e) {
      var r = host.getBoundingClientRect();
      px = ((e.clientX - r.left) / r.width - 0.5) * 2; py = ((e.clientY - r.top) / r.height - 0.5) * 2;
      if (!raf) raf = requestAnimationFrame(function () { host.style.setProperty('--px', px.toFixed(3)); host.style.setProperty('--py', py.toFixed(3)); raf = 0; });
    }, { passive: true });
    host.addEventListener('pointerleave', function () { host.style.setProperty('--px', 0); host.style.setProperty('--py', 0); });
  }

  function tilt(root) {
    if (RM.matches || !FINE.matches) return;
    root.addEventListener('pointermove', function (e) {
      var el = e.target.closest && e.target.closest('[data-lat-tilt]');
      if (!el) return;
      var r = el.getBoundingClientRect();
      var x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      el.style.setProperty('--rx', ((0.5 - y) * 6).toFixed(2) + 'deg');
      el.style.setProperty('--ry', ((x - 0.5) * 8).toFixed(2) + 'deg');
      el.style.setProperty('--mx', (x * 100).toFixed(1) + '%');
      el.style.setProperty('--my', (y * 100).toFixed(1) + '%');
      el.classList.add('is-tilting');
    }, { passive: true });
    root.addEventListener('pointerout', function (e) {
      var el = e.target.closest && e.target.closest('[data-lat-tilt]');
      if (el && !el.contains(e.relatedTarget)) { el.classList.remove('is-tilting'); el.style.removeProperty('--rx'); el.style.removeProperty('--ry'); }
    }, { passive: true });
  }

  function init() {
    document.querySelectorAll('[data-lat-hero]').forEach(function (host) {
      var c = host.querySelector('[data-lat-field]');
      if (c) Field(c, host);
      parallax(host);
    });
    tilt(document);
    document.documentElement.classList.add('lat-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
